import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Check, ChevronDown, Heart, Minus, Plus } from 'lucide-react';
import { Button } from '@dzseguidores/ui';
import { useProduct, useProducts } from '@/hooks/use-catalog';
import { useCroSettings } from '@/hooks/use-cro-settings';
import { useSeo } from '@/hooks/use-seo';
import { useCartStore, MAX_PACKAGES_PER_LINE } from '@/stores/cart-store';
import { NetworkIcon } from '@/components/network-icon';
import { PriceBlock } from '@/components/cro/price-block';
import { TrustBar } from '@/components/cro/trust-bar';
import { CrossSellCard } from '@/components/cro/featured-catalog';
import { ReviewsGrid, ScarcityNote } from '@/components/cro/social-proof';
import { pickCrossSell, serviceLabel } from '@/lib/cro';
import { breadcrumbJsonLd, faqJsonLd, productJsonLd } from '@/lib/seo';
import { api, ApiError } from '@/lib/api-client';
import { useIsAuthenticated } from '@/lib/auth';
import type { ProductPublic } from '@dzseguidores/types';

const faqs = [
  {
    q: 'Quanto tempo leva a entrega?',
    a: 'A maioria dos pedidos inicia em até 30 minutos. O tempo total depende do volume de seguidores ou curtidas.',
  },
  {
    q: 'Preciso informar senha?',
    a: 'Nunca. Apenas o link público do perfil ou publicação no Instagram ou TikTok.',
  },
  {
    q: 'E se houver queda?',
    a: 'Garantia de reposição conforme política do produto, sem burocracia.',
  },
];

function productSeoCopy(product: {
  name: string;
  description: string;
  networkName?: string;
  networkSlug: string;
  serviceTypeSlug: string;
}) {
  const network = product.networkName ?? product.networkSlug;
  const service = serviceLabel(product.serviceTypeSlug);
  return {
    title: `Comprar ${product.name}`,
    description: `${product.description} Compre ${service} ${network} com PIX, entrega monitorada e garantia na DZ Seguidores.`,
    keywords: [
      `comprar ${service} ${network}`.toLowerCase(),
      product.name.toLowerCase(),
      `likes ${network}`.toLowerCase(),
    ],
  };
}

function settingText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

export function ProductPage() {
  const { slug = '' } = useParams();
  const { data, isLoading } = useProduct(slug);
  const addItem = useCartStore((s) => s.addItem);
  const product = data ?? undefined;
  const [packages, setPackages] = useState(1);
  const [favBusy, setFavBusy] = useState(false);
  const [favMsg, setFavMsg] = useState<string | null>(null);
  const loggedIn = useIsAuthenticated();
  const { data: cro } = useCroSettings();
  const { data: catalogData } = useProducts(product?.networkSlug);
  const catalog: ProductPublic[] = catalogData?.items ?? [];

  useSeo(
    product
      ? {
          ...productSeoCopy(product),
          path: `/produto/${product.slug}`,
          type: 'product',
          image: product.imageUrl,
          jsonLd: [
            productJsonLd({
              name: product.name,
              description: product.description,
              slug: product.slug,
              priceCents: product.promoPriceCents ?? product.priceCents,
              compareAtCents: product.promoPriceCents ? product.priceCents : null,
              imageUrl: product.imageUrl,
              networkName: product.networkName,
            }),
            faqJsonLd(faqs),
            breadcrumbJsonLd([
              { name: 'Início', path: '/' },
              {
                name: product.networkName ?? product.networkSlug,
                path:
                  product.networkSlug === 'instagram' || product.networkSlug === 'tiktok'
                    ? `/${product.networkSlug}`
                    : '/catalogo',
              },
              { name: product.name, path: `/produto/${product.slug}` },
            ]),
          ],
        }
      : {
          title: isLoading ? 'Carregando produto' : 'Produto não encontrado',
          description: 'Pacotes de seguidores e curtidas Instagram e TikTok na DZ Seguidores.',
          path: `/produto/${slug}`,
          noindex: !isLoading,
        },
  );

  if (isLoading) {
    return (
      <div className="container-page py-10">
        <p className="text-muted">Carregando produto…</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-page py-10">
        <p>Produto não encontrado.</p>
        <Link to="/catalogo" className="mt-4 inline-block text-sm underline">
          Voltar ao catálogo
        </Link>
      </div>
    );
  }

  const maxPackages = Math.min(
    MAX_PACKAGES_PER_LINE,
    Math.max(1, Math.floor(product.maxQty / Math.max(product.minQty, 1))),
  );
  const qty = Math.min(Math.max(1, packages), maxPackages);
  const unitPrice = product.promoPriceCents ?? product.priceCents;
  const networkPath =
    product.networkSlug === 'instagram' || product.networkSlug === 'tiktok'
      ? `/${product.networkSlug}`
      : '/catalogo';
  const crossSell = pickCrossSell(product, catalog);
  const scarcity = settingText(cro?.['cro.scarcity']);
  const guarantee =
    settingText(cro?.['cro.guarantee']) ||
    'Trabalhamos com política clara de reposição e acompanhamento do pedido. Pagamentos processados com parceiros certificados no Brasil.';

  function addToCart() {
    addItem({
      productId: product!.id,
      slug: product!.slug,
      name: product!.name,
      priceCents: unitPrice,
      quantity: qty,
    });
  }

  async function addFavorite() {
    if (!product) return;
    if (!loggedIn) {
      window.location.assign('/login');
      return;
    }
    setFavBusy(true);
    setFavMsg(null);
    try {
      await api.post('/account/favorites', { productId: product.id });
      setFavMsg('Salvo em Favoritos.');
    } catch (err) {
      setFavMsg(
        err instanceof ApiError && err.status === 409
          ? 'Já está nos favoritos.'
          : err instanceof ApiError
            ? err.message
            : 'Não foi possível salvar.',
      );
    } finally {
      setFavBusy(false);
    }
  }

  return (
    <div className="container-page py-10">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to="/" className="hover:text-fg">
              Início
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to={networkPath} className="hover:text-fg">
              {product.networkName ?? product.networkSlug}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-fg">{product.name}</li>
        </ol>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <div className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted">
            <NetworkIcon
              slug={product.networkSlug}
              name={product.networkName ?? product.networkSlug}
              size={18}
            />
            <span>{product.networkName ?? product.networkSlug}</span>
            <span aria-hidden>·</span>
            <span>{serviceLabel(product.serviceTypeSlug)}</span>
          </div>
          <h1 className="mt-2 text-3xl font-semibold text-fg">{product.name}</h1>
          <p className="mt-4 text-muted">{product.description}</p>

          <PriceBlock
            className="mt-6"
            priceCents={product.priceCents}
            promoPriceCents={product.promoPriceCents}
            size="lg"
          />
          <p className="mt-1 text-sm text-muted">
            Este pacote entrega {product.minQty.toLocaleString('pt-BR')}{' '}
            {serviceLabel(product.serviceTypeSlug)}
          </p>

          {scarcity ? (
            <div className="mt-4">
              <ScarcityNote text={scarcity} />
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted">Nº de pacotes</span>
            <div className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] bg-surface px-2 py-1">
              <button
                type="button"
                className="rounded-lg p-1.5 text-muted hover:bg-[var(--color-bg)] hover:text-fg"
                onClick={() => setPackages((n) => Math.max(1, n - 1))}
                aria-label="Diminuir pacotes"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                min={1}
                max={maxPackages}
                value={qty}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setPackages(Number.isFinite(next) ? next : 1);
                }}
                className="w-14 bg-transparent text-center text-sm font-semibold text-fg outline-none"
                aria-label="Número de pacotes"
              />
              <button
                type="button"
                className="rounded-lg p-1.5 text-muted hover:bg-[var(--color-bg)] hover:text-fg"
                onClick={() => setPackages((n) => Math.min(maxPackages, n + 1))}
                aria-label="Aumentar pacotes"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <span className="text-xs text-muted">máx. {maxPackages}</span>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button size="lg" onClick={addToCart}>
              Adicionar ao carrinho
            </Button>
            <Link to="/checkout" onClick={addToCart}>
              <Button variant="secondary" size="lg">
                Comprar agora
              </Button>
            </Link>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              disabled={favBusy}
              onClick={() => void addFavorite()}
            >
              <Heart className="mr-1.5 h-4 w-4" />
              Favoritar
            </Button>
          </div>
          {favMsg && <p className="mt-2 text-xs text-muted">{favMsg}</p>}

          <p className="mt-4 text-xs text-muted">
            Checkout em minutos · PIX com aprovação instantânea · Sem senha
          </p>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border p-6">
            <h2 className="font-semibold">O que está incluso</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-fg" /> Entrega monitorada
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-fg" /> Suporte em português
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-fg" /> Garantia de reposição
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-fg" /> Sem pedir senha
              </li>
            </ul>
          </div>
          <TrustBar compact />
        </div>
      </div>

      <section className="mt-16">
        <h2 className="text-xl font-semibold">Prova social</h2>
        <div className="mt-6">
          <ReviewsGrid />
        </div>
      </section>

      {crossSell && (
        <section className="mt-16">
          <h2 className="text-xl font-semibold">Complete seu pedido</h2>
          <p className="mt-2 text-sm text-muted">
            Aumente resultado combinando seguidores e curtidas na mesma rede.
          </p>
          <div className="mt-6 max-w-md">
            <CrossSellCard product={crossSell} />
          </div>
        </section>
      )}

      <section className="mt-16">
        <h2 className="text-xl font-semibold">Garantia</h2>
        <p className="mt-3 max-w-2xl text-muted">{guarantee}</p>
      </section>

      <section className="mt-16">
        <h2 className="text-xl font-semibold">Perguntas frequentes</h2>
        <div className="mt-4 divide-y divide-border rounded-xl border border-border">
          {faqs.map((f) => (
            <details key={f.q} className="group px-4 py-3">
              <summary className="flex cursor-pointer list-none items-center justify-between font-medium">
                {f.q}
                <ChevronDown className="h-4 w-4 transition group-open:rotate-180" />
              </summary>
              <p className="mt-2 pb-2 text-sm text-muted">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border glass p-3 md:hidden">
        <div className="flex items-center justify-between gap-3">
          <PriceBlock
            priceCents={product.priceCents}
            promoPriceCents={product.promoPriceCents}
            size="sm"
          />
          <Link to="/checkout" className="flex-1" onClick={addToCart}>
            <Button className="w-full">Comprar agora</Button>
          </Link>
        </div>
      </div>
      <div className="h-16 md:hidden" aria-hidden />
    </div>
  );
}
